// Live per-head pricing from the `pricing` table — the single source of truth
// for what a booking costs (cruise_type + deck → Adult / Child price). Every
// screen that prices a booking goes through here; there is deliberately NO
// hardcoded fallback. If a rate cannot be read, callers must refuse to price
// rather than guess: a booking charged the wrong amount is worse than one that
// cannot be recorded for a moment.
//
// Two read shapes, both mirroring the public site's edge functions:
//   fetchRates(cruise, deck)  — one slot, the `payment` function's create-order
//                               read (filtered by cruise_type + deck).
//   fetchPricingLookup()      — the whole table, the `availability` function's
//                               priceMap read, for screens that price many
//                               cruise/deck combinations at once.
import { supabase } from '../lib/supabase';

// A usable rate pair: adult strictly positive, child non-negative, both finite.
// Anything else (missing row, null price, NaN) is "no price" — never zero.
export function validRates(adult, child) {
  const a = Number(adult);
  const c = Number(child);
  if (!Number.isFinite(a) || a <= 0) return null;
  if (!Number.isFinite(c) || c < 0) return null;
  return { adult: a, child: c };
}

// { [cruise_type]: { [deck]: { adult, child } } } from raw pricing rows.
// Returns null when there are no rows at all, so callers can tell "table
// empty / unreadable" apart from "this one slot has no row".
export function buildPricingLookup(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const lookup = {};
  for (const row of rows) {
    if (!row || !row.cruise_type || !row.deck) continue;
    if (!lookup[row.cruise_type]) lookup[row.cruise_type] = {};
    if (!lookup[row.cruise_type][row.deck]) lookup[row.cruise_type][row.deck] = { adult: null, child: null };
    if (row.passenger_type === 'Adult') lookup[row.cruise_type][row.deck].adult = Number(row.price);
    if (row.passenger_type === 'Child') lookup[row.cruise_type][row.deck].child = Number(row.price);
  }
  return Object.keys(lookup).length > 0 ? lookup : null;
}

// Validated rates for one cruise + deck out of a lookup, or null when the slot
// has no usable row. Safe to call with a null lookup.
export function ratesFor(lookup, cruise, deck) {
  if (!lookup || !cruise || !deck) return null;
  const slot = lookup[cruise]?.[deck];
  if (!slot) return null;
  return validRates(slot.adult, slot.child);
}

// Whole-table read. Resolves { lookup, error } — lookup is null on a query
// error or an empty table.
export async function fetchPricingLookup() {
  const { data, error } = await supabase
    .from('pricing')
    .select('cruise_type, deck, passenger_type, price');
  if (error) return { lookup: null, error };
  return { lookup: buildPricingLookup(data), error: null };
}

// Single-slot read. Resolves { rates, error } — rates is null on a query
// error, a missing Adult/Child row, or an unusable price.
export async function fetchRates(cruise, deck) {
  const { data, error } = await supabase
    .from('pricing')
    .select('passenger_type, price')
    .eq('cruise_type', cruise)
    .eq('deck', deck)
    .in('passenger_type', ['Adult', 'Child']);
  if (error) return { rates: null, error };
  let adult = null, child = null;
  for (const r of data || []) {
    if (r.passenger_type === 'Adult') adult = r.price;
    if (r.passenger_type === 'Child') child = r.price;
  }
  return { rates: validRates(adult, child), error: null };
}

// Standard copy for the refusal, so every screen says the same thing.
export function priceLoadFailedMessage(cruise, deck, what = 'This booking') {
  return `Price couldn't be loaded for ${cruise} · ${deck}. ${what} was not saved. Retry, or check the Pricing screen.`;
}

export function priceMissingMessage(cruise, deck, what = 'This booking') {
  return `No price is set for ${cruise} · ${deck} in the Pricing screen. ${what} was not saved.`;
}

export const PRICE_LOADING_MESSAGE = 'Price is still loading — try again in a moment.';
